import { Component, Input, OnDestroy } from '@angular/core';
import { AnyObject, CurrentMobile, CurrentMobileConfig, Device, Mobile } from '@mri/types';
import { map, mergeMap, Observable, of, Subject, Subscription, tap } from 'rxjs';
import { AuthResourceService, CalcVars, ConfigResourceService, GenericResourceService } from '@mri/app/resources';
import { SourceBaseOriginReport, urlReportMap } from '../chart-view';
import { calcDistanceNauticalMiles } from '@mri/functions';
import { CurrentMobileResourceService } from '../../resources/current-mobile-resource';

interface ResumeData {
  lastAddress?: string;
  alertCount?: number;
  hasIgnition?: boolean;
  workTimeHours?: number;
  lastDeviceTimePowerOff?: string | Date;
  lastDeviceTimePowerOn?: string | Date;
  lastDeviceTime?: string | Date;
  firstDeviceTimePowerOff?: string | Date;
  firstDeviceTimePowerOn?: string | Date;
  distance?: number;
}

@Component({
  selector: 'app-detail-current-day-mobile',
  templateUrl: './resume-current-mobile-day.component.html',
  styleUrl: './resume-current-mobile-day.component.scss'
})
export class ResumeCurrentMobileDayComponent implements OnDestroy {
  private _sub = new Subscription();
  private _consultResumeDay$ = new Subject<string | Date>();
  private _element$ = new Subject<CurrentMobile>();
  private _currentMobileId$ = new Subject<string>();
  private _mobileId$ = new Subject<string>();
  private _deviceId$ = new Subject<string>();
  private _device?: Device;
  private _mobile?: Mobile;

  calcVars!: CalcVars;
  resume: ResumeData = { hasIgnition: true };
  currentMobile?: CurrentMobile;
  config?: CurrentMobileConfig;
  ready: boolean = false;

  @Input() set mobileId(value: string) {
    this._mobileId$
      .next(value);
  }

  @Input() set deviceId(value: string) {
    this._deviceId$
      .next(value);
  }

  @Input() set currentMobileId(value: string) {
    this._currentMobileId$
      .next(value);
  }

  constructor(
    private _genericSrc: GenericResourceService,
    private _configSrc: ConfigResourceService,
    private _authSrc: AuthResourceService,
    private _currentSrc: CurrentMobileResourceService
  ) {
    this._sub.add(
      this._mobileId$
        .pipe(
          mergeMap(id => this._getMobile(id))
        )
        .subscribe()
    );

    this._sub.add(
      this._deviceId$
        .pipe(
          mergeMap(id => this._getDevice(id))
        )
        .subscribe()
    );

    this._sub.add(
      this._currentMobileId$
        .pipe(
          mergeMap(id => this._obtainCurrentAndSave(id))
        )
        .subscribe()
    );
    this._sub.add(
      this._element$
        .pipe(
          tap(() => console.log('Actualizando desde socket...')),
          mergeMap(element => this._updateOfSocket(element))
        )
        .subscribe()
    );

    this._sub.add(
      this._consultResumeDay$
        .pipe(
          mergeMap(() => this.obtainReportsOfPosition()),
          mergeMap(() => this.obtainReportsOfAlarms()),
          tap(() => this.ready = true),
        ).subscribe()
    )

  }

  obtainReportsOfPosition(): Observable<AnyObject> {
    const url = urlReportMap[SourceBaseOriginReport.POSITION];

    const metrics = [
      'workTimeHours', 'distance', 'maxSpeed', 'firstDeviceTimePowerOn', 'firstDeviceTimePowerOff',
      'lastDeviceTimePowerOn', 'lastDeviceTimePowerOff', 'lastDeviceTime', 'lastAddress'
    ];
    const filter = this._getBasicFilter();

    const metricFilter = (metrics.reduce( (acc, key) =>
        Object.assign(acc, { [`$metric:${key}`]: '1' }), {})
    );
    Object.assign(filter, metricFilter);

    return this._genericSrc.getElements(url, { filter })
      .pipe(
        tap( response => Object.assign(this.resume, response.objects[0]))
      );
  }

  obtainReportsOfAlarms(): Observable<AnyObject> {
    const url = urlReportMap[SourceBaseOriginReport.ALARM_EVENT];

    const metrics = [
      'total'
    ];
    const filter = this._getBasicFilter();

    const metricFilter = (metrics.reduce( (acc, key) =>
        Object.assign(acc, { [`$metric:${key}`]: '1' }), {})
    );
    Object.assign(filter, metricFilter);

    return this._genericSrc.getElements<AnyObject>(url, { filter })
      .pipe(
        tap( response => Object.assign(this.resume, { alertCount: response.objects[0]?.total ?? 0 }))
      );
  }

  ngOnDestroy() {
    this._sub.unsubscribe();
  }

  private _getCurrentFromMobile(id: string): Observable<CurrentMobile> {
    return this._genericSrc.getElement<CurrentMobile>(['mobiles', id, 'current'].join('/'))
      .pipe(
        map(response => response.object)
      );
  }

  private _getCurrentFromDevice(id: string): Observable<CurrentMobile> {
    return this._genericSrc.getElement<CurrentMobile>(['devices', id, 'current'].join('/'))
      .pipe(
        map(response => response.object)
      );
  }

  private _getCurrent(id: string): Observable<CurrentMobile> {
    return this._genericSrc.getElement<CurrentMobile>(['mobiles', 'currents', id].join('/'))
      .pipe(
        map(response => response.object)
      );
  }

  private _getCurrentFromAny(id: string, source?: 'mobile' | 'device'): Observable<CurrentMobile> {
    if (source === 'mobile')
      return this._getCurrentFromMobile(id);

    if (source === 'device')
      return this._getCurrentFromDevice(id);

    return this._getCurrent(id);
  }

  private _getBasicFilter(){

    const startDeviceTime = this.calcVars.startToday;
    const endDeviceTime = new Date(startDeviceTime.getTime() + (1000 * 60 * 60 * 24));
    const filter: Record<string, AnyObject> = {
      startDeviceTime, endDeviceTime,
    };

    const cm = this.currentMobile ?? {} as CurrentMobile;

    const deviceId = this._device?._id ?? cm?.deviceId;
    const mobileId = this._mobile?._id ?? cm?.mobile?._id;

    if(mobileId) Object.assign(filter, { mobileId });
    if(deviceId) Object.assign(filter, { deviceId });

    return filter;
  }

  private _obtainCurrentAndSave(id: string, source?: 'mobile' | 'device'): Observable<AnyObject> {
    return this._getCurrentFromAny(id, source)
      .pipe(
        tap(object => this.currentMobile = object),
        mergeMap(() => this._configSrc.getCalcVars()),
        tap(() => this.config = this.currentMobile?.config ?? this._mobile?.config ?? {}),
        tap(vars => this.calcVars = vars ?? {} as CalcVars),
        //mergeMap(() => this.getReportsOfPosition()),
        //tap(response => Object.assign(this.resume, response?.objects?.[0] || {})),

        tap(() =>
          this.resume.hasIgnition = this.config?.sensors?.find(f => f.key === 'ignition')?.enable
        ),
        tap( () => this._consultResumeDay$.next(new Date())),
        tap(auth => auth && this._initSocket())
      );
  }

  private _getDevice(id: string): Observable<AnyObject> {
    return this._genericSrc.getElement<Device>('devices', id)
      .pipe(
        map(response => response.object),
        tap(object => this._device = object),
        mergeMap(() => this._obtainCurrentAndSave(id, 'device')),
      );
  }

  private _getMobile(id: string): Observable<AnyObject> {
    return this._genericSrc.getElement<Mobile>('mobiles', id)
      .pipe(
        map(response => response.object),
        tap(object => this._mobile = object),
        mergeMap(() => this._obtainCurrentAndSave(id, 'mobile')),
      );
  }

  private _initSocket() {
    const id = this.currentMobile?._id;

    if(!id) return;
    this._sub.add(
      this._currentSrc.subscribeElement(id)
        .pipe(
          tap(element => this._element$.next(element))
        )
        .subscribe()
    )

  }

  private _updateOfSocket(current: CurrentMobile): Observable<AnyObject>{
    if(
      this.resume.lastDeviceTime === current.position?.deviceTime ||
      this.resume.lastDeviceTime !== current.position?.prev?.deviceTime
    ) {
      this._consultResumeDay$.next(new Date())
      return of(null);
    }

    const position = current.position;
    const prev = position?.prev;

    if(!position) return of(null);
    if(!prev) return of(null);

    if(position.sensors?.['ignition'] !== prev.sensors?.['ignition'] ) {
      if(position.sensors?.['ignition'] ){
        this.resume.lastDeviceTimePowerOn = position.deviceTime;
      }else {
        this.resume.lastDeviceTimePowerOn = position.deviceTime;
      }
    }

    if(position.sensors?.['ignition'] && prev.sensors?.['ignition']){
      this.resume.workTimeHours = (this.resume.workTimeHours || 0) + (new Date(position.deviceTime).getTime() -  new Date(prev.deviceTime).getTime())/ (1000 * 60 * 60);
    }

    this.resume.lastAddress = position.address;
    this.resume.lastDeviceTime = position.deviceTime;
    this.resume.distance = (this.resume.distance  || 0) + calcDistanceNauticalMiles(position.geometric, prev.geometric)

    return this.obtainReportsOfAlarms();
  }

}
